/**
 * Scene Storage Service - Persist scene builder state in localStorage
 * 
 * Handles saving and loading of scene builder state including:
 * - Selected stickers and their positions/properties
 * - Global settings (size, color, zoom)
 * - Scene metadata (last modified, sticker count)
 */

import logger from '../utils/logger.js';

const STORAGE_KEY = 'clothedeye_scene_builder_state';
const STORAGE_VERSION = '1.0.0';

class SceneStorageService {
  /**
   * Save current scene state to localStorage
   * @param {Object} sceneState - Complete scene state object
   * @param {Array} sceneState.selectedStickers - Array of sticker objects in the scene
   * @param {string} sceneState.globalSize - Global size setting
   * @param {string} sceneState.globalColor - Global color setting
   * @param {number} sceneState.zoom - Zoom level
   * @param {boolean} sceneState.sidebarCollapsed - Sidebar state
   * @param {number} sceneState.sidebarWidth - Sidebar width
   */
  saveSceneState(sceneState) {
    try {
      const stateToSave = {
        version: STORAGE_VERSION,
        timestamp: new Date().toISOString(),
        stickerCount: sceneState.selectedStickers?.length || 0,
        data: {
          selectedStickers: sceneState.selectedStickers || [],
          globalSize: sceneState.globalSize || 'MICRO',
          globalColor: sceneState.globalColor || 'Black (Matte)',
          zoom: sceneState.zoom || 100,
          sidebarCollapsed: sceneState.sidebarCollapsed || false,
          sidebarWidth: sceneState.sidebarWidth || 400
        }
      };

      const serializedState = JSON.stringify(stateToSave);
      localStorage.setItem(STORAGE_KEY, serializedState);
      
      return true;
    } catch (error) {
      console.error('❌ Failed to save scene state to localStorage:', error);
      return false;
    }
  }

  /**
   * Load scene state from localStorage
   * @returns {Object|null} Parsed scene state or null if not found/invalid
   */
  loadSceneState() {
    try {
      const serializedState = localStorage.getItem(STORAGE_KEY);
      if (!serializedState) {
        return null;
      }

      const parsedState = JSON.parse(serializedState);
      
      // Validate state structure
      if (!parsedState.version || !parsedState.data) {
        logger.warn('⚠️ Invalid scene state structure, ignoring saved state');
        this.clearSceneState();
        return null;
      }

      // Check version compatibility (future-proofing)
      if (parsedState.version !== STORAGE_VERSION) {
        logger.warn(`⚠️ Scene state version mismatch (saved: ${parsedState.version}, current: ${STORAGE_VERSION})`);
        // For now, still try to load but log the mismatch
      }

      return parsedState.data;
    } catch (error) {
      console.error('❌ Failed to load scene state from localStorage:', error);
      this.clearSceneState(); // Clear corrupted data
      return null;
    }
  }

  /**
   * Clear saved scene state from localStorage
   * @returns {boolean} Success status
   */
  clearSceneState() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('❌ Failed to clear scene state from localStorage:', error);
      return false;
    }
  }

  /**
   * Check if there is a saved scene state
   * @returns {boolean} True if saved state exists
   */
  hasSavedState() {
    try {
      const serializedState = localStorage.getItem(STORAGE_KEY);
      if (!serializedState) return false;
      
      const parsedState = JSON.parse(serializedState);
      return !!(parsedState.data && parsedState.data.selectedStickers?.length > 0);
    } catch (error) {
      console.error('❌ Error checking for saved state:', error);
      return false;
    }
  }

  /**
   * Get summary of saved scene state (without loading full data)
   * @returns {Object|null} Scene summary or null
   */
  getSceneSummary() {
    try {
      const serializedState = localStorage.getItem(STORAGE_KEY);
      if (!serializedState) return null;
      
      const parsedState = JSON.parse(serializedState);
      if (!parsedState.data) return null;

      return {
        stickerCount: parsedState.stickerCount || 0,
        timestamp: parsedState.timestamp || 'Unknown',
        version: parsedState.version || 'Unknown'
      };
    } catch (error) {
      console.error('❌ Error getting scene summary:', error);
      return null;
    }
  }

  /**
   * Debounced save function to avoid excessive localStorage writes
   * @param {Object} sceneState - Scene state to save
   * @param {number} delay - Debounce delay in milliseconds (default: 1000)
   */
  debouncedSave = null;
  setupDebouncedSave(delay = 1000) {
    return (sceneState) => {
      if (this.debouncedSave) {
        clearTimeout(this.debouncedSave);
      }
      
      this.debouncedSave = setTimeout(() => {
        this.saveSceneState(sceneState);
      }, delay);
    };
  }
}

// Export singleton instance
const sceneStorageService = new SceneStorageService();
export default sceneStorageService;